// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Spin } from 'antd';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import moment from 'moment';

import './chart-amount.scss';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import DatePickerComponent from '../../general/date-picker';
import { get } from '../../../api';
import { capitalizeFirstLetter } from '../../../utils/general';

export default function ChartAmount(props: any) {
  const { customerSelected } = props;
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { Title } = Typography;
  const colors = scaleOrdinal(schemeCategory10).range();
  const [maxTotal, setMaxTotal] = useState(0);
  const [width, setWidth] = useState('100%');
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const startDate = moment().startOf('month').subtract(11, 'month');
    const endDate = moment().endOf('month').subtract(0, 'month');
    filterDate([startDate, endDate], customerSelected);
  }, [customerSelected]);

  const calculateTotal = (data) => {
    return data.reduce((sum, item) => sum + (item.totals?.MXN || 0), 0);
  };

  const filterDate = React.useCallback(
    async (date: any, customer, currency = 'MXN') => {
      setIsLoading(true);
      let date2 = {
        startDate: date[0].format('YYYY-MM-DD'),
        endDate: date[1].format('YYYY-MM-DD'),
      };
      let filters = `/Customers/${
        customer ? customer : customerSelected
      }/totalInvoiced?start=${date2.startDate}&end=${date2.endDate}`;

      let totalInvoicesResponse = await get<string>(filters);
      if (totalInvoicesResponse.success) {
        totalInvoicesResponse.data.forEach((element: any, index: any) => {
          element['key'] = index;
          element['Total'] =
            element.totals[currency] >= 0
              ? element.totals[currency].toFixed(2)
              : element.totals[currency].toFixed(2) * -1;
          element['DispTotal'] =
            element.totals[currency] >= 0
              ? '$' +
                new Intl.NumberFormat('en', {
                  minimumFractionDigits: 2,
                }).format(element.totals[currency])
              : '$-' +
                new Intl.NumberFormat('en', {
                  minimumFractionDigits: 2,
                }).format(element.totals[currency] * -1);
          element['DispDate'] = capitalizeFirstLetter(element.displayDate);
        });

        setData(totalInvoicesResponse.data);

        const total = calculateTotal(totalInvoicesResponse.data);
        setTotalAmount(total);
      }
      setIsLoading(false);
    },
    [customerSelected]
  );

  useEffect(() => {
    if (data && data.length > 0) {
      const totals = data.map((obj) => obj.Total);
      const max = Math.max(...totals);
      setMaxTotal(parseInt(max));
      if (data.length < 7) {
        setWidth('100%');
      } else if (data.length >= 7 && data.length < 12) {
        setWidth('125%');
      } else if (data.length >= 12) {
        setWidth('150%');
      }
    }
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${label}`}</p>
          <p className="label">{`Total: ${payload[0].payload.DispTotal}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Row className="chart-amount">
        <Col span={24} className="chart-amount__title">
          <Title level={3}>Monto total facturado (MXN)</Title>
        </Col>
        <Col span={24} className="chart-amount__dropdown">
          <DatePickerComponent filterDate={filterDate} />
        </Col>
        <Col span={24} className="chart-amount__total">
          <Title level={4}>
            Total Facturado: $
            {new Intl.NumberFormat('en', { minimumFractionDigits: 2 }).format(
              totalAmount
            )}
          </Title>
        </Col>
        <Col span={24}>
          <Spin tip={<p>Cargando...</p>} spinning={isLoading}>
            <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
              <ResponsiveContainer width={width} height={450}>
                <BarChart
                  data={data}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="DispDate" />
                  <YAxis
                    padding={{ top: 30 }}
                    width={100}
                    domain={[0, maxTotal]}
                    tickFormatter={(value) =>
                      '$' +
                      new Intl.NumberFormat('en', {
                        minimumFractionDigits: 2,
                      }).format(value)
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="Total"
                    fill="#000000"
                    label={{
                      position: 'top',
                      dataKey: 'DispTotal',
                    }}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[index % 20] as string}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Spin>
        </Col>
      </Row>
    </>
  );
}
